/**
 * MobileSupervisionsActionsList — flat action cards ( <lg ). Batch 6.4.
 * Same /supervisions/actions payload the desktop uses. Manager gets an
 * inline Select for status; care worker sees static text (backend
 * require_role("admin","manager") is authoritative). Tap "Open" reuses
 * the same openEdit(record) handler for the parent record.
 */
import React from "react";
import { ChevronRight, User } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PRIORITY_TONE = {
  low:    "bg-slate-50 text-slate-600 border-slate-300",
  medium: "bg-amber-50 text-amber-800 border-amber-300",
  high:   "bg-red-50 text-red-800 border-red-300",
};

export default function MobileSupervisionsActionsList({ actions, records, canManage, onMark, onOpen }) {
  if (!actions || actions.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center" data-testid="mobile-sup-actions-empty">
        <div className="text-sm font-semibold text-slate-700">No open actions. 🎉</div>
      </div>
    );
  }
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-3" data-testid="mobile-sup-actions">
      <div className="text-xs text-slate-500 tabular-nums" data-testid="mobile-sup-actions-count">
        {actions.length} action{actions.length === 1 ? "" : "s"}
      </div>
      <ul className="space-y-2" data-testid="mobile-sup-actions-list">
        {actions.map((a) => {
          const overdue = a.due_date && a.due_date < today;
          return (
            <li key={a.id} data-testid={`mobile-sup-action-row-${a.id}`} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-sm font-semibold text-slate-900" data-testid={`mobile-sup-action-text-${a.id}`}>
                {a.action || "—"}
              </div>
              <div className="text-[11px] text-slate-600 mt-1 flex items-center gap-2 flex-wrap">
                <span className="flex items-center gap-1"><User className="w-3 h-3 text-slate-400" />{a.responsible_person_name || "Unassigned"}</span>
                <span className="text-slate-400">·</span>
                <span className={`tabular-nums ${overdue ? "text-red-700 font-semibold" : ""}`} data-testid={`mobile-sup-action-due-${a.id}`}>
                  Due {a.due_date || "—"}
                </span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                From <span className="text-slate-700">{a.staff_name || "—"}</span>
                {a.held_date && <span className="text-slate-400"> · held {a.held_date}</span>}
              </div>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${PRIORITY_TONE[a.priority] || PRIORITY_TONE.medium}`}
                  data-testid={`mobile-sup-action-priority-${a.id}`} data-priority={a.priority}>
                  {a.priority || "medium"}
                </span>
                {canManage ? (
                  <Select value={a.status || "open"} onValueChange={(v) => onMark?.(a.supervision_id, a.id, { status: v })}>
                    <SelectTrigger className="h-11 min-h-[44px] text-xs w-[130px]" data-testid={`mobile-sup-action-status-${a.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="no_longer_required">No longer required</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-[11px] text-slate-700" data-testid={`mobile-sup-action-static-${a.id}`}>{a.status || "open"}</span>
                )}
                <button type="button"
                  onClick={() => { const r = (records || []).find((x) => x.id === a.supervision_id); if (r) onOpen?.(r); }}
                  data-testid={`btn-mobile-sup-action-open-${a.id}`}
                  className="ml-auto min-h-[44px] inline-flex items-center gap-1 text-xs font-medium text-cyan-700">
                  Open <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
